import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { toast } from "sonner";
import {
  RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle2, Wand2, ArrowRight, Zap, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const sevColor = (s) => (s === "high" ? "text-red-300 border-red-400/60" :
                         s === "medium" ? "text-amber-300 border-amber-400/60" :
                         "text-white/50 border-white/20");

export default function SASchemaDrift() {
  const { tenantId, tenants } = useSuperAdminTenant();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [detailByTenant, setDetailByTenant] = useState({}); // tenant_id -> scan doc, keyed so expanding row A then scanning row B never clobbers A's panel
  const [scanning, setScanning] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => {
    const params = {};
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    saApi.get("/schema-drift", { params }).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [tenantId]);

  const toggle = async (row) => {
    if (expanded === row.tenant_id) { setExpanded(null); return; }
    setExpanded(row.tenant_id); setDetailLoading(true);
    try {
      const { data } = await saApi.get(`/schema-drift/${row.tenant_id}`);
      setDetailByTenant((prev) => ({ ...prev, [row.tenant_id]: data }));
    } catch { setDetailByTenant((prev) => ({ ...prev, [row.tenant_id]: null })); }
    finally { setDetailLoading(false); }
  };

  const runScan = async (tid) => {
    setScanning(tid);
    try {
      const { data } = await saApi.post("/schema-drift/scan", { tenant_id: tid });
      setDetailByTenant((prev) => ({ ...prev, [tid]: data }));
      toast.success(`Scan complete: ${data.drift_count} issue(s) across ${data.dashboards_scanned} dashboards`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(null); }
  };

  const applyOne = async (tid, drift) => {
    try {
      await saApi.post("/schema-drift/apply", {
        tenant_id: tid, dashboard_id: drift.dashboard_id, widget_id: drift.widget_id,
        fields: drift.suggested_fix?.fields || {}, keys: drift.suggested_fix?.keys || {},
      });
      toast.success("Applied");
      const { data } = await saApi.get(`/schema-drift/${tid}`);
      setDetailByTenant((prev) => ({ ...prev, [tid]: data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
  };

  const applyAll = async (tid) => {
    const drifts = detailByTenant[tid]?.drifts || [];
    const applyItems = drifts.filter((d) => !d.applied && d.suggested_fix
      && (Object.keys(d.suggested_fix.fields || {}).length + Object.keys(d.suggested_fix.keys || {}).length) > 0)
      .map((d) => ({ dashboard_id: d.dashboard_id, widget_id: d.widget_id, fields: d.suggested_fix.fields || {}, keys: d.suggested_fix.keys || {} }));
    if (!applyItems.length) { toast.message("Nothing to apply"); return; }
    try {
      const { data } = await saApi.post("/schema-drift/apply-all", { tenant_id: tid, items: applyItems });
      toast.success(`Applied ${data.applied}/${data.total}`);
      const { data: d2 } = await saApi.get(`/schema-drift/${tid}`);
      setDetailByTenant((prev) => ({ ...prev, [tid]: d2 }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk apply failed"); }
  };

  return (
    <SuperAdminShell title="Schema Drift">
      <div className="px-5 md:px-8 py-6 space-y-4">
        <p className="text-[11px] text-white/40 max-w-2xl">
          Platform-wide drift summary across every tenant. Expand a row to run a scan and apply
          suggested fixes directly — the same monitor tenant admins see in their own Admin Console.
        </p>
        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm" data-testid="sa-drift-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3 text-right">Dashboards scanned</th>
                <th className="px-4 py-3 text-right">Issues</th>
                <th className="px-4 py-3">Last scan</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items === null ? (
                <tr><td colSpan={5} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">No tenants in scope</td></tr>
              ) : items.map((row) => (
                <React.Fragment key={row.tenant_id}>
                  <tr data-testid="sa-drift-row" className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => toggle(row)}>
                    <td className="px-4 py-2 text-white flex items-center gap-1.5">
                      {expanded === row.tenant_id ? <ChevronDown className="w-3.5 h-3.5 text-white/50" /> : <ChevronRight className="w-3.5 h-3.5 text-white/50" />}
                      {row.tenant_name}
                    </td>
                    <td className="px-4 py-2 text-right text-white/70">{row.dashboards_scanned}</td>
                    <td className="px-4 py-2 text-right">
                      {row.drift_count > 0
                        ? <Badge variant="outline" className="text-[10px] text-amber-300 border-amber-400/50">{row.drift_count}</Badge>
                        : row.scanned_at ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-300 border-0">clean</Badge> : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-4 py-2 text-white/50 text-xs">{row.scanned_at ? new Date(row.scanned_at).toLocaleString() : "never scanned"}</td>
                    <td className="px-4 py-2 text-right">
                      <Button data-testid={`sa-drift-scan-${row.tenant_id}`} size="sm" variant="ghost"
                              onClick={(e) => { e.stopPropagation(); runScan(row.tenant_id); }} disabled={scanning === row.tenant_id}
                              className="h-7 text-xs text-cyan-300 hover:text-white hover:bg-cyan-400/10">
                        {scanning === row.tenant_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Scan
                      </Button>
                    </td>
                  </tr>
                  {expanded === row.tenant_id && (
                    <tr>
                      <td colSpan={5} className="px-4 pb-4">
                        <DriftDetail
                          tenantId={row.tenant_id}
                          detail={detailByTenant[row.tenant_id]}
                          loading={detailLoading}
                          onApplyOne={applyOne}
                          onApplyAll={applyAll}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SuperAdminShell>
  );
}

function DriftDetail({ tenantId, detail, loading, onApplyOne, onApplyAll }) {
  if (loading) return <div className="py-4 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading scan…</div>;
  if (!detail) return <div className="py-4 text-white/40 text-sm">No scan yet for this tenant. Click Scan above.</div>;
  const drifts = detail.drifts || [];
  const hasFixable = drifts.some((d) => !d.applied && d.suggested_fix
    && (Object.keys(d.suggested_fix.fields || {}).length + Object.keys(d.suggested_fix.keys || {}).length) > 0);
  if (drifts.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-400/30 bg-emerald-400/5 text-emerald-300 px-4 py-3 text-sm">
        <CheckCircle2 className="w-4 h-4" />All widgets resolve cleanly.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden">
      {hasFixable && (
        <div className="flex justify-end px-3 py-2 bg-white/5">
          <Button data-testid={`sa-drift-apply-all-${tenantId}`} size="sm" onClick={() => onApplyAll(tenantId)}
                  className="h-7 text-xs text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
            <Zap className="w-3.5 h-3.5 mr-1" />Apply all suggested fixes
          </Button>
        </div>
      )}
      <table className="w-full text-xs">
        <thead className="bg-white/5 text-white/40"><tr className="text-left">
          <th className="px-3 py-2">Severity</th><th className="px-3 py-2">Dashboard</th><th className="px-3 py-2">Widget</th>
          <th className="px-3 py-2">Reason</th><th className="px-3 py-2">Fix</th><th className="px-3 py-2"></th>
        </tr></thead>
        <tbody>
          {drifts.map((d, i) => {
            const sf = d.suggested_fix || {};
            const fields = sf.fields || {}; const keys = sf.keys || {};
            const hasSuggestion = Object.keys(fields).length + Object.keys(keys).length > 0;
            return (
              <tr key={i} className="border-t border-white/5">
                <td className="px-3 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${sevColor(d.severity)}`}><AlertTriangle className="w-3 h-3 mr-1" />{d.severity}</Badge></td>
                <td className="px-3 py-2 text-white/80">{d.dashboard_name}</td>
                <td className="px-3 py-2 text-white/80">{d.widget_title}</td>
                <td className="px-3 py-2 text-white/50 max-w-[280px]">{d.reason}</td>
                <td className="px-3 py-2">
                  {hasSuggestion ? (
                    <div className="space-y-0.5 font-mono text-[10px]">
                      {[...Object.entries(fields), ...Object.entries(keys)].map(([o, n]) => (
                        <div key={o} className="flex items-center gap-1 text-white/60">
                          <span className="text-red-300 line-through">{o}</span><ArrowRight className="w-3 h-3" /><span className="text-emerald-300">{n}</span>
                        </div>
                      ))}
                    </div>
                  ) : <span className="text-white/30 italic">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {hasSuggestion && !d.applied ? (
                    <Button data-testid={`sa-drift-apply-${tenantId}-${i}`} size="sm" onClick={() => onApplyOne(tenantId, d)}
                            className="h-6 text-[10px] bg-cyan-400/20 text-cyan-200 hover:bg-cyan-400/30">
                      <Wand2 className="w-3 h-3 mr-1" />Apply
                    </Button>
                  ) : d.applied ? (
                    <span className="text-emerald-300 text-[10px] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Applied</span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
